import axios from "axios";
import jwtDecode from "jwt-decode";
import {
  IS_LOADING,
  SET_ERROR,
  SET_DATA_ITEM,
  SET_SUCCESS,
  SET_APP_VERSION,
} from "./types";
import { ActionTree } from "vuex";
import { State } from "./state";
import router from "@/router";
import AuthService from "@/services/AuthService";
import i18n from "@/i18n";
import { reactive, toRef } from "vue";
import { ToastSeverity } from "primevue/api";

/**
 * @param store
 * @param credentials
 * @returns {Promise<void>}
 */
export const signIn = (store: any, credentials: Record<string, any>): void => {
  store.commit(IS_LOADING, true);
  axios
    .post("/authentication_token", credentials)
    .then(({ data, headers }) => {
      AuthService.setLogin(data.token);
      const decodedToken: any = jwtDecode(data.token);
      console.log(decodedToken);
      if (
        !decodedToken.roles?.find(
          (r: any) => r === "ROLE_ADMIN" || r === "ROLE_SUPER_ADMIN"
        )
      ) {
        window.location.replace(
          process.env.VUE_APP_BACKEND_BASE_URL + "front/asset/1/9"
        );
        return;
      }
      store.commit(SET_DATA_ITEM, decodedToken);
      if (data.version) {
        store.commit(SET_APP_VERSION, data.version);
      }

      const route: any = toRef(reactive(router), "currentRoute").value;
      if (route.query?.redirect) {
        router.replace(route.query.redirect).catch((error) => {
          console.error("Error on redirect", error.message);
        });
      } else {
        router.replace({ name: "TwoFactorAuth" }).catch((error) => {
          console.error("Error on redirect", error.message);
        });
      }
      //TODO to be added logo on login
    })
    .catch(({ response }) => {
      const message = response?.data["hydra:description"]
        ? response.data["hydra:description"]
        : response?.data?.message
        ? response?.data?.message
        : "Unbekannter Fehler";
      store.commit(SET_ERROR, message);
      store.dispatch(
        "globalMessage/setMessage",
        {
          text: message,
          type: "error",
        },
        { root: true }
      );
    })
    .finally(() => {
      store.commit(IS_LOADING, false);
    });
};

/**
 * @param store
 * @returns {Promise<void>}
 */
export const signOut = async (store: any) => {
  return await axios
    .get("/logout")
    .then((response) => {
      store.commit(SET_SUCCESS, response);
      store.commit(IS_LOADING, false);
      AuthService.clear();
      window.location.href = "/login";
    })
    .catch((error) => {
      store.commit(SET_ERROR, error);
      store.commit(IS_LOADING, false);
    })
    .finally(() => {
      store.commit(IS_LOADING, false);
      // AuthService.logout();
      // window.location.href = "login";
    });
};

/**
 * @param store
 * @param username
 * @returns {Promise<void>}
 */
export const resetPasswordRequest = (store: any, username: string): void => {
  store.commit(IS_LOADING, true);
  axios
    .post("/api/reset_password/request", username)
    .then(({ data }) => {
      router.push({ name: "Login" }).catch((error) => {
        console.error(error.message);
      });
      store.dispatch(
        "globalMessage/setMessage",
        {
          text: i18n.global.t("auth.rest_password_request"),
          type: "success",
        },
        { root: true }
      );
    })
    .catch(({ response }) => {
      const message = response?.data["hydra:description"]
        ? response.data["hydra:description"]
        : response?.data?.message
        ? response?.data?.message
        : "Unbekannter Fehler";
      store.commit(SET_ERROR, message);
      store.dispatch(
        "globalMessage/setMessage",
        {
          text: message,
          type: "error",
        },
        { root: true }
      );
    })
    .finally(() => {
      store.commit(IS_LOADING, false);
    });
};

/**
 * @param store
 * @param resetData
 * @returns {Promise<void>}
 */
export const resetPasswordChange = (
  store: any,
  resetData: Record<string, any>
): void => {
  store.commit(IS_LOADING, true);
  axios
    .post("/api/reset_password/change", resetData)
    .then(({ data }) => {
      router.push({ name: "Login" }).catch((error) => {
        console.error(error.message);
      });
      store.dispatch(
        "globalMessage/setMessage",
        {
          text: i18n.global.t("auth.rest_password_change"),
          type: "success",
        },
        { root: true }
      );
    })
    .catch(({ response }) => {
      const message = response?.data["hydra:description"]
        ? response.data["hydra:description"]
        : response?.data?.message
        ? response?.data?.message
        : "Unbekannter Fehler";
      store.commit(SET_ERROR, message);
      store.dispatch(
        "globalMessage/setMessage",
        {
          text: message,
          type: "error",
        },
        { root: true }
      );
    })
    .finally(() => {
      store.commit(IS_LOADING, false);
    });
};

export const twoFactorAuthCheck = (store: any, code: number): void => {
  store.commit(IS_LOADING, true);
  axios
    .get(`/api/qr-check/${code}`)
    .then(({ data, headers }) => {
      router.replace({ name: "Home" }).catch((error) => {
        console.error("Error on redirect", error.message);
      });
    })
    .catch(({ response }) => {
      store.dispatch(
        "globalMessage/setMessage",
        {
          text: i18n.global.t("auth.two_factor_code_invalid"),
          type: "error",
        },
        { root: true }
      );
      setTimeout(() => {
        signOut(store);
      }, 4000);
    })
    .finally(() => {
      store.commit(IS_LOADING, false);
    });
};

export default <ActionTree<State, any>>{
  signIn,
  signOut,
  resetPasswordRequest,
  resetPasswordChange,
  twoFactorAuthCheck,
};
